/**
 * InMemoryAdapter — reference implementation of PersistenceAdapter.
 * ---------------------------------------------------------------------
 * Zero dependencies. Deterministic. The Persistence Framework has no
 * knowledge that this adapter exists — it consumes only the contract.
 *
 * Storage schema
 *   Map<key, { value: string, meta: object, storedAt: number }>
 */
class InMemoryAdapter {
  constructor() {
    this.name = 'in-memory';
    this._store = new Map();
  }

  async get(key) {
    if (typeof key !== 'string' || key.length === 0) return null;
    const entry = this._store.get(key);
    return entry ? entry.value : null;
  }

  async set(key, value, meta = {}) {
    if (typeof key !== 'string' || key.length === 0) {
      throw new Error('InMemoryAdapter.set: key must be a non-empty string');
    }
    if (typeof value !== 'string') {
      throw new Error('InMemoryAdapter.set: value must be a string');
    }
    this._store.set(key, { value, meta: { ...meta }, storedAt: Date.now() });
  }

  async update(key, patcher, meta = {}) {
    const current = await this.get(key);
    const next = patcher ? patcher(current) : current;
    if (typeof next !== 'string') {
      throw new Error('InMemoryAdapter.update: patcher must return a string');
    }
    await this.set(key, next, meta);
    return next;
  }

  async remove(key) {
    return this._store.delete(key);
  }

  async list(prefix) {
    const p = typeof prefix === 'string' ? prefix : '';
    const keys = [];
    for (const key of this._store.keys()) {
      if (p === '' || key.startsWith(p)) keys.push(key);
    }
    return keys;
  }

  async clearExpired(nowMs, prefix) {
    // The adapter does not interpret values (governance rule). Expiry
    // detection is a Framework concern: it enumerates via list(), reads
    // via get(), then instructs remove(). This method exists to allow
    // future adapters to perform bulk expiry natively; for the in-memory
    // implementation it is a no-op that returns 0.
    void nowMs; void prefix;
    return 0;
  }

  async healthCheck() {
    return { ok: true, adapter: this.name, details: { size: this._store.size } };
  }
}

export { InMemoryAdapter };
